<template>
	<div class="pb-4">
		<div
			:class="[ borderValidationClass, borderClasses]"
			class="relative"
		>
			<label
				:for="id"
				class="absolute text-sm text-gray-500 capitalize transition-all focus-within:left-0"
				:class="floatLabel ? '-translate-y-5' : 'top-3'"
			>
				<span>
					{{ label }}
				</span>
				<span
					v-if="required && showRequiredAstrisk"
					:class="[
						labelSize,
						'text-red-500 lowercase'
					]"
				>
					*
				</span>
			</label>
			<select
				:id="id"
				:value="modelValue"
				required
				:aria-label="ariaLabel"
				class="w-full h-[42px] bg-transparent border-0 rounded appearance-none focus:ring focus:ring-transparent"
				:class="[ placeholderSelected && !hidePlaceholder ? 'text-gray-500' : 'text-black' ]"
				@input="updateValue"
				@focus="handleFocus"
				@blur="handleBlur"
			>
				<option
					v-if="useDefaultPlaceholderElement"
					value=""
					disabled
					selected
				>
					{{ placeholder }}
				</option>
				<option
					v-for="option in options"
					:key="option.value"
					:value="option.value"
					:disabled="option.disabled || false"
					:selected="option.selected || false"
					:class="[ option.disabled ? 'text-gray-500' : 'text-black' ]"
				>
					{{ option.label }}
				</option>
			</select>
		</div>

		<div
			v-if="showErrorMessageOnInvalid"
			class="h-[20px]"
		>
			<Transition name="fade-and-slide">
				<div
					v-if="showErrorMessage"
					class="pt-1 pl-1 text-xs italic text-red-500"
				>
					{{ errorMessage }}
				</div>
			</Transition>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ERROR, NONE } from '@/constants/form/states.js'
import { inputStateValidator } from '@/validators/text-inputs.js'
import type { Option } from '@/components/form/types'

const props = defineProps({
	state: {
		type: String,
		default: NONE,
		validator: inputStateValidator
	},
	options: {
		type: Array as PropType<Option[]>,
		required: true
	},
	modelValue: {
		type: String,
		required: true
	},
	label: {
		type: String,
		default: ''
	},
	id: {
		type: String,
		required: true
	},
	ariaLabel: {
		type: String,
		required: true
	},
	placeholder: {
		type: String,
		default: 'Select an option' // NOTE: This default does not have language localization, so ensure it's set appropriately.
	},
	hidePlaceholder: {
		type: Boolean,
		default: false
	},
	required: {
		type: Boolean,
		default: false
	},
	labelSize: {
		type: String,
		default: 'text-sm'
	},
	showRequiredAstrisk: {
		type: Boolean,
		default: true
	},
	showErrorMessageOnInvalid: {
		type: Boolean,
		default: true
	},
	errorMessage: {
		type: String,
		default: 'Please enter a valid response'
	},
	borderClasses: {
		type: String,
		default: 'border-b-2 border-gray-300'
	}
})
const { state, options, modelValue, label, placeholder, hidePlaceholder, id, required, labelSize, showRequiredAstrisk, showErrorMessageOnInvalid, errorMessage } = toRefs(props)
const emit = defineEmits([ 'update:modelValue' ])
const updateValue = (event: Event) => {
	const target = event.target as HTMLInputElement
	emit('update:modelValue', target?.value)
}
const enableErrorMessage = ref(false)
const focused = ref(false)
const placeholderSelected = ref(false)

const hasPlaceholderInOptions = computed(() => {
	return options.value.some(option => option.value === '')
})

const useDefaultPlaceholderElement = computed(() => {
	return !hasPlaceholderInOptions.value && !hidePlaceholder.value
})

const handleFocus = () => {
	focused.value = true
}
const handleBlur = () => {
	focused.value = false
	enableErrorMessage.value = true
}

const isExpanded = computed(() => {
	return state.value === ERROR
})
const showErrorMessage = computed(() => {
	return isExpanded.value && required.value && showErrorMessageOnInvalid.value && enableErrorMessage.value
})
const borderValidationClass = computed(() => {
	return {
		'focus:border-red-500 border-red-500': showErrorMessage.value
	}
})
const floatLabel = computed(() => {
	return options.value
		.some(option => option.selected) ||
		placeholderSelected.value ||
		!!modelValue.value.length ||
		!!focused.value
})

watch(modelValue, () => {
	placeholderSelected.value = !(options.value.some(option => option.selected) || modelValue.value !== '')
}, { immediate: true })

</script>
